import { Box, IconButton } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useRef } from "react"
import { useFormContext } from "react-hook-form"

import { type GlPostingAccountOption, useGlPostingAccountOptions } from "../hooks/useGlPostingAccountOptions"
import type { GlTransactionFormValues } from "../schemas/glTransactionForm"
import { filterAvailableGlPostingAccountOptions } from "../utils/glTransactionFormUtils"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { GlTransactionRowType } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"

const TRANSACTION_TYPE_OPTIONS = [
  { value: GlTransactionRowType.Debit, label: "Debit" },
  { value: GlTransactionRowType.Credit, label: "Credit" },
]

interface TransactionLineRowProps {
  index: number
  disabled?: boolean
  initialOption?: GlPostingAccountOption
  excludedAccountIds: string[]
  canRemove: boolean
  onRemove: () => void
}

export const TransactionLineRow: FC<TransactionLineRowProps> = ({
  index,
  disabled,
  initialOption,
  excludedAccountIds,
  canRemove,
  onRemove,
}) => {
  const { control } = useFormContext<GlTransactionFormValues>()

  const { options, isFetching, onLoadMore, onQueryChange, ensureOption } = useGlPostingAccountOptions(
    initialOption ? [initialOption] : undefined
  )

  const accountOptions = filterAvailableGlPostingAccountOptions(options, excludedAccountIds)

  const handleAccountChange = useCallback(
    (value: string | null) => {
      if (!value) return
      const option = options.find((o) => o.value === value)
      if (option) ensureOption(option)
    },
    [options, ensureOption]
  )

  const listboxRef = useRef<HTMLUListElement>(null)

  const handleListboxScroll = useScrollLoadMore({
    fetching: isFetching,
    loadMore: onLoadMore,
    scrollContainerRef: listboxRef,
  })

  return (
    <Box className='grid grid-cols-[160px_1fr_160px_40px] items-start gap-2'>
      <FormSelect
        control={control}
        name={`transactionLines.${index}.type`}
        options={TRANSACTION_TYPE_OPTIONS}
        label=''
        disabled={disabled}
        data-testid={`gl-transaction.form-dialog.line-type-${index}`}
      />
      <FormAutocomplete
        control={control}
        name={`transactionLines.${index}.glAccountId`}
        label=''
        placeholder='Search account'
        options={accountOptions}
        required
        loading={isFetching}
        onChange={handleAccountChange}
        onInputChange={onQueryChange}
        slotProps={{ listbox: { ref: listboxRef, onScroll: handleListboxScroll } }}
        disabled={disabled}
        data-testid={`gl-transaction.form-dialog.line-account-${index}`}
      />
      <FormNumericField
        control={control}
        name={`transactionLines.${index}.amount`}
        label=''
        decimalScale={2}
        allowNegative={false}
        disabled={disabled}
        data-testid={`gl-transaction.form-dialog.line-amount-${index}`}
      />
      <IconButton
        size='small'
        disabled={disabled || !canRemove}
        onClick={onRemove}
        data-testid={`gl-transaction.form-dialog.line-remove-button-${index}`}
        className='mt-1'
      >
        <DeleteOutlined fontSize='small' />
      </IconButton>
    </Box>
  )
}
